export interface GeneralSettingsSchema {
  // Site Information
  siteName: string;
  siteNameArabic: string;
  contactEmail: string;
  contactPhone: string;
  location?: string;
  siteDescription: string;
  siteDescriptionArabic: string;
  metaTitle: string;
  metaTitleArabic: string;
  metaKeywords: string;
  metaKeywordsArabic: string;
  metaDescription: string;
  metaDescriptionArabic: string;

  // Footer Information
  footerText: string;
  footerTextArabic: string;
  footerDescription: string;
  footerDescriptionArabic: string;
  preFooterTitle: string;
  preFooterTitleArabic: string;
  preFooterDescription: string;
  preFooterDescriptionArabic: string;

  // Images
  preFooterImage?: string;
  loginImage?: string;
  featuredRestaurantImage?: string;

  // App Store URLs
  itunesUrl: string;
  playStoreUrl: string;

  // Status Settings
  couponStatus: boolean;
  otpVerificationStatus: {
    status: boolean;
    twilioSid: string;
    twilioToken: string;
    twilioFrom: string;
  };
  mailVerificationStatus: boolean;
  noContactDeliveryStatus: boolean;
  selfPickupStatus: boolean;
  preOrderStatus: {
    status: boolean;
    merchantAcceptanceTimeInSeconds?: number;
    processStartBeforeMinutes?: number;
    minimumAdvanceMinutes?: number;
    maximumAdvanceDays?: number;
  };
  showOutOfStockProducts: boolean;
  showStock: boolean;
  myRewardsToggle: boolean;
  myOffersToggle: boolean;
  referFriendToggle: boolean;
  preOrderToggle: boolean;

  // Delivery Settings
  deliveryFeeStatus: boolean;
  // deliveryFeeType: "common" | "perKilometer";
  commonDeliveryFee: number;
  feePerKM: number;
  deliveryBoyKilometerRange: number;

  // Service Fee Settings
  serviceFeeType: 'fixedFee' | 'percentageFee';
  serviceFee: number;

  // Peak Hours Settings
  peakHoursDeliveryFeeStatus: {
    status: boolean;
    peakHoursConfiguration: {
      sunday: {
        enabled: boolean;
        timeRanges: Array<{
          startTime: string;
          endTime: string;
          additionalFee: number;
        }>;
      };
      monday: {
        enabled: boolean;
        timeRanges: Array<{
          startTime: string;
          endTime: string;
          additionalFee: number;
        }>;
      };
      tuesday: {
        enabled: boolean;
        timeRanges: Array<{
          startTime: string;
          endTime: string;
          additionalFee: number;
        }>;
      };
      wednesday: {
        enabled: boolean;
        timeRanges: Array<{
          startTime: string;
          endTime: string;
          additionalFee: number;
        }>;
      };
      thursday: {
        enabled: boolean;
        timeRanges: Array<{
          startTime: string;
          endTime: string;
          additionalFee: number;
        }>;
      };
      friday: {
        enabled: boolean;
        timeRanges: Array<{
          startTime: string;
          endTime: string;
          additionalFee: number;
        }>;
      };
      saturday: {
        enabled: boolean;
        timeRanges: Array<{
          startTime: string;
          endTime: string;
          additionalFee: number;
        }>;
      };
    };
  };

  // Order and Cart Settings
  orderHistoryExpire: {
    status: boolean;
    orderHistoryExpireDuration: number;
  };
  abandonedCartMailStatus: {
    status: boolean;
    sendAbandonedCartMailAfterHours: number;
  };

  // Referral Settings
  referFriends: {
    status: boolean;
    offerPercentage: number;
    maxReferralAmount: number;
  };

  // Security Settings
  passwordProtection: boolean;
  showCaptcha: boolean;
  invalidLoginSuspendCustomer: boolean;

  // Restaurant Settings
  featuredRestaurant: {
    status: boolean;
    featuredPricePerDay: number;
  };
  commonCommissionPercentage: number;
  commonDeliveryRadius: {
    status: boolean;
    deliveryRadius: number;
    deliveryRadiusType: 'kilometers' | 'miles';
  };

  // Currency and Time Settings
  currencyConverterApiKey: string;
  timeToPreparePreorderMinutes: number;
  timeZone: string;
  dateTimeFormat: string;
  emailStatus: boolean;
  smsStatus: boolean;

  // Loyalty Rewards
  loyaltyRewardsStatus: {
    status: boolean;
    minimumTargetPoints: number;
    currencyToPointsRatio: number;
    pointsPerPurchaseAmount: number;
    redeemPointValue: number;
  };

  // Pre-order Settings
  preOrderMerchantAcceptanceTimeInSeconds?: number;
  preOrderProcessStartBeforeMinutes?: number;
  preOrderMinimumAdvanceMinutes?: number;
  preOrderMaximumAdvanceDays?: number;
  emergencyDriverBonusAmount?: number;
  emergencyDriverBonusPercentage?: number;
  driverUnavailableTimeoutExtension?: number;
  enableAutomaticTimeoutExtension?: boolean;

  // Order Alert Settings
  orderAlertSettings?: {
    merchantAccepted?: number;
    orderPreparing?: number;
    readyForPickup?: number;
  };
}

export const transformFormDataToSchema = (
  formValues: any
): GeneralSettingsSchema => {
  return {
    // Site Information
    siteName: formValues.siteName || 'string',
    siteNameArabic: formValues.siteNameArabic || 'string',
    contactEmail: formValues.contactEmail || 'user@example.com',
    contactPhone: formValues.contactPhone || '60',
    location: formValues.location || undefined,
    siteDescription: formValues.siteDescription || 'string',
    siteDescriptionArabic: formValues.siteDescriptionArabic || 'string',
    metaTitle: formValues.metaTitle || 'string',
    metaTitleArabic: formValues.metaTitleArabic || 'string',
    metaKeywords: formValues.metaKeywords || 'string',
    metaKeywordsArabic: formValues.metaKeywordsArabic || 'string',
    metaDescription: formValues.metaDescription || 'string',
    metaDescriptionArabic: formValues.metaDescriptionArabic || 'string',

    // Footer Information
    footerText: formValues.footerText || 'string',
    footerTextArabic: formValues.footerTextArabic || 'string',
    footerDescription: formValues.footerDescription || 'string',
    footerDescriptionArabic: formValues.footerDescriptionArabic || 'string',
    preFooterTitle: formValues.preFooterTitle || 'string',
    preFooterTitleArabic: formValues.preFooterTitleArabic || 'string',
    preFooterDescription: formValues.preFooterDescription || 'string',
    preFooterDescriptionArabic:
      formValues.preFooterDescriptionArabic || 'string',

    // Images
    preFooterImage: formValues.preFooterImage ?? undefined,
    loginImage: formValues.loginImage ?? undefined,
    featuredRestaurantImage: formValues.featuredRestaurantImage ?? undefined,

    // App Store URLs
    itunesUrl: formValues.itunesUrl || 'string',
    playStoreUrl: formValues.playStoreUrl || 'string',

    // Status Settings
    couponStatus: formValues.couponStatus === 'Enable',
    otpVerificationStatus: {
      status: formValues.otpVerificationStatus === 'Enable',
      twilioSid: formValues.twilioSid || 'string',
      twilioToken: formValues.twilioToken || 'string',
      twilioFrom: formValues.twilioFrom || '+61',
    },
    mailVerificationStatus: formValues.mailVerificationStatus === 'Enable',
    noContactDeliveryStatus: formValues.noContactDeliveryStatus === 'Enable',
    selfPickupStatus: formValues.selfPickupStatus === 'Enable',
    preOrderStatus: {
      status: formValues.preOrderStatus === 'Enable',
      merchantAcceptanceTimeInSeconds:
        formValues.preOrderMerchantAcceptanceTimeInSeconds
          ? parseInt(formValues.preOrderMerchantAcceptanceTimeInSeconds, 10)
          : undefined,
      processStartBeforeMinutes: formValues.preOrderProcessStartBeforeMinutes
        ? parseInt(formValues.preOrderProcessStartBeforeMinutes, 10)
        : undefined,
      minimumAdvanceMinutes: formValues.preOrderMinimumAdvanceMinutes
        ? parseInt(formValues.preOrderMinimumAdvanceMinutes, 10)
        : undefined,
      maximumAdvanceDays: formValues.preOrderMaximumAdvanceDays
        ? parseInt(formValues.preOrderMaximumAdvanceDays, 10)
        : undefined,
    },
    showOutOfStockProducts: formValues.showOutOfStockProducts === 'Yes',
    showStock: formValues.showStock === 'Yes',
    myRewardsToggle: formValues.myRewardsToggle === 'Enable',
    myOffersToggle: formValues.myOffersToggle === 'Enable',
    referFriendToggle: formValues.referFriendToggle === 'Enable',
    preOrderToggle: formValues.preOrderToggle === 'Enable',

    // Delivery Settings
    deliveryFeeStatus: formValues.deliveryFeeStatus === 'Enable',
    // deliveryFeeType: formValues.deliveryFeeType === 'Common Delivery Fee' ? "common" : "perKilometer",
    commonDeliveryFee: parseFloat(formValues.commonDeliveryFee) || 0,
    feePerKM: parseFloat(formValues.feePerKilometer) || 0, // Using commonDeliveryFee for fee per km
    deliveryBoyKilometerRange:
      parseInt(formValues.deliveryBoyKilometerRange) || 0,

    // Service Fee Settings
    serviceFeeType:
      formValues.serviceFeeType === 'Fixed Fee' ? 'fixedFee' : 'percentageFee',
    serviceFee: parseFloat(formValues.serviceFee) || 0,
    // Peak Hours Settings
    peakHoursDeliveryFeeStatus: {
      status: formValues.peakHoursStatus === 'Enable',
      peakHoursConfiguration: {
        sunday: {
          enabled: formValues.peakHoursData?.sunday?.length > 0,
          timeRanges:
            formValues.peakHoursData?.sunday?.map((item: any) => ({
              startTime: item.startTime || 'string',
              endTime: item.endTime || 'string',
              additionalFee: parseFloat(item.amount) || 0,
            })) || [],
        },
        monday: {
          enabled: formValues.peakHoursData?.monday?.length > 0,
          timeRanges:
            formValues.peakHoursData?.monday?.map((item: any) => ({
              startTime: item.startTime || 'string',
              endTime: item.endTime || 'string',
              additionalFee: parseFloat(item.amount) || 0,
            })) || [],
        },
        tuesday: {
          enabled: formValues.peakHoursData?.tuesday?.length > 0,
          timeRanges:
            formValues.peakHoursData?.tuesday?.map((item: any) => ({
              startTime: item.startTime || 'string',
              endTime: item.endTime || 'string',
              additionalFee: parseFloat(item.amount) || 0,
            })) || [],
        },
        wednesday: {
          enabled: formValues.peakHoursData?.wednesday?.length > 0,
          timeRanges:
            formValues.peakHoursData?.wednesday?.map((item: any) => ({
              startTime: item.startTime || 'string',
              endTime: item.endTime || 'string',
              additionalFee: parseFloat(item.amount) || 0,
            })) || [],
        },
        thursday: {
          enabled: formValues.peakHoursData?.thursday?.length > 0,
          timeRanges:
            formValues.peakHoursData?.thursday?.map((item: any) => ({
              startTime: item.startTime || 'string',
              endTime: item.endTime || 'string',
              additionalFee: parseFloat(item.amount) || 0,
            })) || [],
        },
        friday: {
          enabled: formValues.peakHoursData?.friday?.length > 0,
          timeRanges:
            formValues.peakHoursData?.friday?.map((item: any) => ({
              startTime: item.startTime || 'string',
              endTime: item.endTime || 'string',
              additionalFee: parseFloat(item.amount) || 0,
            })) || [],
        },
        saturday: {
          enabled: formValues.peakHoursData?.saturday?.length > 0,
          timeRanges:
            formValues.peakHoursData?.saturday?.map((item: any) => ({
              startTime: item.startTime || 'string',
              endTime: item.endTime || 'string',
              additionalFee: parseFloat(item.amount) || 0,
            })) || [],
        },
      },
    },

    // Order and Cart Settings
    orderHistoryExpire: {
      status: formValues.orderHistoryExpire === 'Enable',
      orderHistoryExpireDuration:
        parseInt(formValues.orderHistoryDuration) || 1,
    },
    abandonedCartMailStatus: {
      status: formValues.abandonedCartMailStatus === 'Enable',
      sendAbandonedCartMailAfterHours:
        parseInt(formValues.sendMailAfter) || 168,
    },

    // Referral Settings
    referFriends: {
      status: formValues.referFriends === 'Enable',
      offerPercentage: parseFloat(formValues.offerPercentage) || 100,
      maxReferralAmount: parseFloat(formValues.maxReferralAmount) || 0,
    },

    // Security Settings
    passwordProtection: formValues.passwordProtection === 'Enable',
    showCaptcha: formValues.showCaptcha === 'Enable',
    invalidLoginSuspendCustomer: formValues.invalidLoginSuspend === 'Enable',

    // Restaurant Settings
    featuredRestaurant: {
      status: formValues.featuredRestaurant === 'Enable',
      featuredPricePerDay: parseFloat(formValues.featuredPrice) || 0,
    },
    commonCommissionPercentage: parseFloat(formValues.commonCommission) || 100,
    commonDeliveryRadius: {
      status: formValues.commonDeliveryRadius === 'Enable',
      deliveryRadius: parseFloat(formValues.deliveryRadiusInKm) || 0,
      deliveryRadiusType:
        formValues.deliveryRadiusType === 'Kilometers' ? 'kilometers' : 'miles',
    },

    // Currency and Time Settings
    currencyConverterApiKey: formValues.apiKeyForCurrencyConverter || 'string',
    timeToPreparePreorderMinutes:
      parseInt(formValues.timePeriodForPreorder) || 1440,
    timeZone: formValues.timeZone || 'string',
    dateTimeFormat: formValues.dateTimeFormat || 'string',
    emailStatus: formValues.emailStatus === 'Enable',
    smsStatus: formValues.smsStatus === 'Enable',

    // Loyalty Rewards
    loyaltyRewardsStatus: {
      status: formValues.loyaltyRewardsStatus === 'Active',
      minimumTargetPoints: parseFloat(formValues.minimumTargetPoints) || 0,
      currencyToPointsRatio: parseFloat(formValues.usdOneEquals) || 0,
      pointsPerPurchaseAmount: parseFloat(formValues.usdOneEquals) || 0, // Using same value as currencyToPointsRatio
      redeemPointValue: parseFloat(formValues.redeemPointValue) || 0,
    },

    // Pre-order Settings (moved to preOrderStatus object above)
    emergencyDriverBonusAmount: formValues.emergencyDriverBonusAmount
      ? parseFloat(formValues.emergencyDriverBonusAmount)
      : undefined,
    emergencyDriverBonusPercentage: formValues.emergencyDriverBonusPercentage
      ? parseFloat(formValues.emergencyDriverBonusPercentage)
      : undefined,
    driverUnavailableTimeoutExtension:
      formValues.driverUnavailableTimeoutExtension
        ? parseInt(formValues.driverUnavailableTimeoutExtension, 10)
        : undefined,
    enableAutomaticTimeoutExtension:
      formValues.enableAutomaticTimeoutExtension === 'Enable',

    // Order Alert Settings
    orderAlertSettings: {
      merchantAccepted: formValues.merchantAccepted
        ? parseInt(formValues.merchantAccepted, 10)
        : undefined,
      orderPreparing: formValues.orderPreparing
        ? parseInt(formValues.orderPreparing, 10)
        : undefined,
      readyForPickup: formValues.readyForPickup
        ? parseInt(formValues.readyForPickup, 10)
        : undefined,
    },
  };
};
